import React from 'react';
import {
  Box, HStack, Icon, Text, VStack,
} from '@gluestack-ui/themed';
import { AlertCircle, Check } from 'lucide-react-native';

import type { LobbyStepDescriptor } from './types';

export type LobbyStepperProps = {
  steps: LobbyStepDescriptor[];
};

/**
 * Left vertical rail showing the whole Lobby journey: done (check), active
 * (brand bar + filled dot), upcoming (dim). State is conveyed by icon + label +
 * position, never colour alone (student UX §2.2, §7).
 */
export function LobbyStepper({ steps }: LobbyStepperProps) {
  return (
    <VStack
      testID="oral-exam-lobby-stepper"
      space="xs"
      bg="$bgCard"
      borderWidth={1}
      borderColor="$borderLight"
      borderRadius="$lg"
      p="$2"
    >
      {steps.map((step, index) => {
        const isActive = step.status === 'active';
        const isDone = step.status === 'done';
        const isAttention = step.status === 'attention';

        return (
          <HStack
            key={step.key}
            testID={`oral-exam-lobby-step-${step.key}`}
            accessibilityRole="text"
            accessibilityState={{ selected: isActive }}
            alignItems="center"
            space="md"
            px="$3"
            py="$3"
            borderRadius="$md"
            borderLeftWidth={3}
            borderLeftColor={isActive ? '$brandBase' : 'transparent'}
            bg={isActive ? '$bgBrandSubtle' : 'transparent'}
          >
            <Box
              w={28}
              h={28}
              borderRadius="$full"
              alignItems="center"
              justifyContent="center"
              borderWidth={isDone || isAttention ? 0 : 2}
              borderColor={isActive ? '$brandBase' : '$borderMedium'}
              bg={isActive ? '$brandBase' : 'transparent'}
            >
              {isDone ? (
                <Icon
                  as={Check}
                  size="sm"
                  color="$statusSuccess"
                  testID={`oral-exam-lobby-step-${step.key}-check`}
                />
              ) : isAttention ? (
                <Icon
                  as={AlertCircle}
                  size="sm"
                  color="$statusWarning"
                  testID={`oral-exam-lobby-step-${step.key}-attention`}
                />
              ) : (
                <Text
                  size="xs"
                  fontWeight="bold"
                  color={isActive ? '$textOnBrand' : '$textDisabled'}
                >
                  {index + 1}
                </Text>
              )}
            </Box>

            <Text
              size="sm"
              fontWeight={isActive ? 'semibold' : 'normal'}
              color={isActive ? '$textHeading' : isDone ? '$textBody' : '$textDisabled'}
              numberOfLines={1}
            >
              {step.label}
            </Text>
          </HStack>
        );
      })}
    </VStack>
  );
}
